/**
 * "On track" bucket card: open RMAs moving forward on their own. Same interaction as its siblings —
 * card row opens a floating popup of sections, picks add removable chips, selections apply immediately
 * and are mutually exclusive with the other buckets.
 *
 * The card total is NOT the sum or union of the sections: the parent is the residual "open minus Error
 * minus Manual", so it can sit below them (sections overlap) or above them (an RMA with no section).
 * Hence the row renders from `onTrackTotal`, and there is no "All On track" row — see the popup.
 */
import { observer } from "mobx-react";
import "./rma-list-on-track-bucket.scss";
import useDashboardTranslation from "../../../../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { ON_TRACK_TYPE } from "../../../../../../../../../services/api/rma-list-service";
import { RmaListBucketFiltersStore } from "../../../../../../../rma-list-page/stores/rma-list-bucket-filters-store";
import RmaListOnTrackPopup from "./components/rma-list-on-track-popup/rma-list-on-track-popup";
import RmaListOnTrackBucketCardRow from "./components/rma-list-on-track-bucket-card-row/rma-list-on-track-bucket-card-row";
import RmaListOnTrackBucketChips from "./components/rma-list-on-track-bucket-chips/rma-list-on-track-bucket-chips";
import useBucketPopup from "../../../../../../hooks/use-bucket-popup";

interface IProps {
    bucketFiltersStore: RmaListBucketFiltersStore;
    disabled: boolean;
    onApply: () => void;
}

/** Maps an On track section value to its dashboard-translation key (scope: dashboard-returns-page). */
const ON_TRACK_LABEL_KEY: Record<string, string> = {
    [ON_TRACK_TYPE.AWAITING_PAYMENT]: "ON_TRACK_AWAITING_PAYMENT",
    [ON_TRACK_TYPE.RETURN_NOT_SHIPPED]: "ON_TRACK_RETURN_NOT_SHIPPED",
    [ON_TRACK_TYPE.RETURN_IN_TRANSIT]: "ON_TRACK_RETURN_IN_TRANSIT",
    [ON_TRACK_TYPE.AUTOMATION_SCHEDULED]: "ON_TRACK_AUTOMATION_SCHEDULED",
};

const RmaListOnTrackBucket: React.FC<IProps> = observer(({ bucketFiltersStore, disabled, onApply }) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const { isOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } = useBucketPopup(disabled);

    const selected = bucketFiltersStore.selectedOnTrack;
    const hasSelection = selected.length > 0;

    const getLabel = (value: string) => getTranslation(ON_TRACK_LABEL_KEY[value] ?? value, scope);
    const getCount = (value: string) =>
        bucketFiltersStore.onTrackOptions.find((option) => option.value === value)?.count ?? 0;

    const toggleSection = (value: string) => {
        bucketFiltersStore.toggleOnTrack(value);
        onApply();
    };

    const cardActive = isOpen || hasSelection;
    // Empty bucket (nothing open is progressing on its own) stays visible but dimmed, unless the
    // merchant is actively interacting with it (open / has a selection).
    const isEmpty = bucketFiltersStore.onTrackTotal === 0 && !cardActive;

    return (
        <div className="rma-list-on-track-bucket">
            <div className={`rma-list-on-track-bucket__card${cardActive ? " active" : ""}${isEmpty ? " empty" : ""}${disabled ? " disabled" : ""}`}>
                <RmaListOnTrackBucketCardRow
                    label={getTranslation("ON_TRACK", scope)}
                    total={bucketFiltersStore.onTrackTotal}
                    loading={bucketFiltersStore.areCountersLoading}
                    open={isOpen}
                    disabled={disabled}
                    triggerRef={refs.setReference}
                    triggerProps={getReferenceProps()}
                />

                {hasSelection && (
                    <RmaListOnTrackBucketChips
                        selected={selected}
                        getLabel={getLabel}
                        getCount={getCount}
                        disabled={disabled}
                        onRemove={toggleSection}
                    />
                )}
            </div>

            {isOpen && (
                <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="rma-list-on-track-bucket__popup"
                    {...getFloatingProps()}
                >
                    <RmaListOnTrackPopup
                        bucketFiltersStore={bucketFiltersStore}
                        getLabel={getLabel}
                        onToggle={toggleSection}
                    />
                </div>
            )}
        </div>
    );
});

export default RmaListOnTrackBucket;
